import React from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  LogIn,
  LogOut,
  User,
} from 'lucide-react';

/**
 * StudentAttendanceCard - Card component for displaying student attendance status
 * @param {Object} student - Student data
 * @param {Object} attendance - Attendance record for the student
 * @param {Function} onCheckIn - Callback for check-in action
 * @param {Function} onCheckOut - Callback for check-out action
 * @param {Function} onMarkLate - Callback for mark late action
 * @param {Function} onMarkAbsent - Callback for mark absent action
 * @param {Function} onViewDetails - Callback to view student details
 */
const StudentAttendanceCard = ({
  student,
  attendance,
  onCheckIn,
  onCheckOut,
  onMarkLate,
  onMarkAbsent,
  onViewDetails,
}) => {
  const status = attendance?.status || 'not_marked';
  const checkInTime = attendance?.checkInTime;
  const checkOutTime = attendance?.checkOutTime;

  // Get initials for avatar
  const getInitials = () => {
    const first = student.firstName?.[0] || '';
    const last = student.lastName?.[0] || '';
    return (first + last).toUpperCase();
  };

  // Status configuration
  const statusConfig = {
    present: {
      label: 'Present',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      badgeColor: 'bg-green-100 text-green-700',
    },
    absent: {
      label: 'Absent',
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      badgeColor: 'bg-red-100 text-red-700',
    },
    late: {
      label: 'Late',
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      badgeColor: 'bg-yellow-100 text-yellow-700',
    },
    not_marked: {
      label: 'Not Marked',
      icon: AlertCircle,
      color: 'text-gray-400',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      badgeColor: 'bg-gray-100 text-gray-600',
    },
  };

  const currentStatus = statusConfig[status] || statusConfig.not_marked;
  const StatusIcon = currentStatus.icon;

  // Format time display
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isCheckedIn = status === 'present' || status === 'late';
  const isCheckedOut = !!checkOutTime;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all">
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            {/* Avatar */}
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              {student.photoUrl ? (
                <img
                  src={student.photoUrl}
                  alt={`${student.firstName} ${student.lastName}`}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-purple-700 font-semibold text-lg">
                  {getInitials()}
                </span>
              )}
            </div>

            {/* Student Info */}
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {student.firstName} {student.lastName}
              </h3>
              <p className="text-xs text-gray-500">
                {student.className || 'No class assigned'}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${currentStatus.badgeColor}`}
          >
            <StatusIcon size={12} className="mr-1" />
            {currentStatus.label}
          </span>
        </div>

        {/* Time Info */}
        <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
          <div className="flex items-center">
            <LogIn size={12} className="mr-1 text-gray-400" />
            <span>In: {formatTime(checkInTime)}</span>
          </div>
          <div className="flex items-center">
            <LogOut size={12} className="mr-1 text-gray-400" />
            <span>Out: {formatTime(checkOutTime)}</span>
          </div>
        </div>

        {/* Notes if any */}
        {attendance?.notes && (
          <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
            <span className="font-medium">Note:</span> {attendance.notes}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50 rounded-b-lg">
        <button
          onClick={() => onViewDetails?.(student)}
          className="flex items-center space-x-1 px-3 py-2 min-h-[44px] text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
        >
          <User size={14} />
          <span>Details</span>
        </button>

        <div className="flex items-center space-x-1">
          {/* Check In Button - Show if not marked or absent */}
          {(status === 'not_marked' || status === 'absent') && (
            <button
              onClick={() => onCheckIn?.(student)}
              className="flex items-center space-x-1 px-3 py-2 min-h-[44px] text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors"
            >
              <LogIn size={14} />
              <span>Check In</span>
            </button>
          )}

          {/* Check Out Button - Show if checked in but not checked out */}
          {isCheckedIn && !isCheckedOut && (
            <button
              onClick={() => onCheckOut?.(student)}
              className="flex items-center space-x-1 px-3 py-2 min-h-[44px] text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
            >
              <LogOut size={14} />
              <span>Check Out</span>
            </button>
          )}

          {/* Mark Late - Show if present */}
          {status === 'present' && (
            <button
              onClick={() => onMarkLate?.(student)}
              className="flex items-center space-x-1 px-3 py-2 min-h-[44px] text-xs font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded transition-colors"
            >
              <Clock size={14} />
              <span>Late</span>
            </button>
          )}

          {/* Mark Absent - Show if not marked */}
          {status === 'not_marked' && (
            <button
              onClick={() => onMarkAbsent?.(student)}
              className="flex items-center space-x-1 px-3 py-2 min-h-[44px] text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded transition-colors"
            >
              <XCircle size={14} />
              <span>Absent</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAttendanceCard;
